import Image from 'next/image';
import { ErrorLayoutProps } from './ErrorLayout.types';
import { text, vstack } from '@onbeam/business-styled-system/patterns';
import { css, cx } from '@onbeam/business-styled-system/css';
import { breakout } from '@/styles';

export const ErrorLayout = ({
  title = 'error',
  description = 'sorry, we encountered an unexpected error.',
  errorCode,
  children,
}: ErrorLayoutProps) => (
  <div
    className={cx(
      breakout(),
      vstack({
        textAlign: 'center',
        align: 'center',
        position: 'relative',
        overflow: 'hidden',
        '--header-size': '4.625rem',
        h: '[calc(100dvh - var(--header-size))]',
        md: {
          '--header-size': '4.625rem',
          mt: '[calc(var(--header-size) * -1)]',
          minH: 'screen',
        },
        xl: {
          '--header-size': '6.25rem',
        },
      }),
    )}
  >
    <div
      className={vstack({
        pt: '12',
        px: '6',
        align: 'center',
        gap: '3',
        md: {
          justifyContent: 'center',
          pt: '0',
          gap: '6',
          px: '8',
          h: 'full',
        },
      })}
    >
      <h1
        className={text({
          style: 'subtitle-xl',
          md: { textStyle: 'headline-lg', maxW: '[32rem]' },
          lg: {
            maxW: '[unset]',
          },
        })}
      >
        {title}
      </h1>
      <p
        className={text({
          style: 'body-lg',
          color: 'mono.tertiary',
          md: { textStyle: 'subtitle-sm', maxW: '[25rem]' },
          lg: {
            maxW: '[unset]',
          },
        })}
      >
        {description}
        {!!errorCode && (
          <>
            <br />
            Error code: {errorCode}
          </>
        )}
      </p>
      {children}
    </div>
    <div
      className={css({
        position: 'relative',
        mt: '0',
        h: 'full',
        w: 'full',
        pointerEvents: 'none',
        userSelect: 'none',
        zIndex: -1,
        overflow: 'hidden',
        md: {
          position: 'absolute',
          inset: '0',
          top: '[var(--header-size)]',
        },
        xl: {
          top: '[var(--header-size)]',
        },
      })}
    >
      <Image
        src="/images/letters-scattered.png"
        alt=""
        height={1600}
        width={1500}
        className={css({
          position: 'absolute',
          left: '[50%]',
          transform: 'translateX(-50%) rotate(-90deg)',
          aspectRatio: '1600/1500',
          minW: '[150vw]',
          md: {
            w: '[93.75rem]',
            minW: '[unset]',
            left: '0',
            top: '[-13vw]',
            transform: 'translate(-64.5%) rotate(12.23deg)',
          },
          xl: {
            top: '[-10vw]',
          },
        })}
      />
      <Image
        src="/images/letters-scattered.png"
        alt=""
        height={1600}
        width={1500}
        className={css({
          position: 'absolute',
          right: '0',
          top: '[-7.5vw]',
          w: '[93.75rem]',
          aspectRatio: '1600/1500',
          transform: 'translate(67%)',
          mdDown: {
            display: 'none',
          },
          xl: {
            top: '[-5vw]',
          },
        })}
      />
    </div>
  </div>
);
